import { useEffect, useState } from "react";
import { PageHero } from "@/components/PageHero";
import { ProjectCard } from "@/components/ProjectCard";
import { ConsultButton } from "@/components/CtaButtons";
import { FinalCta } from "@/sections/HomeVoice";
import { useLanguage } from "@/i18n/LanguageContext";
import { PROJECT_LOCATIONS, PROJECT_TYPES } from "@/data/content";
import { IMAGES } from "@/data/images";
import { api } from "@/lib/api";

const matchesType = (p, type) => {
  if (type === "All") return true;
  if (type === "G+4–G+6") return ["G+4", "G+5", "G+6", "G+4–G+6"].includes(p.category);
  return p.category === type;
};

const matchesLocation = (p, loc) => {
  if (loc === "All") return true;
  if (loc === "Surrounding Areas") return !["Bengaluru", "Tumakuru"].includes(p.location);
  return p.location === loc;
};

const FilterBtn = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    aria-pressed={active}
    onClick={onClick}
    className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
      active ? "bg-charcoal text-gold" : "bg-charcoal/5 text-charcoal/60 hover:bg-charcoal/10 hover:text-charcoal"
    }`}
  >
    {children}
  </button>
);

export default function Projects() {
  const { t } = useLanguage();
  const [projects, setProjects] = useState(null);
  const [location, setLocation] = useState("All");
  const [type, setType] = useState("All");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  const filtered = (projects || []).filter((p) => matchesLocation(p, location) && matchesType(p, type));

  return (
    <>
      <PageHero eyebrow={t("projects.eyebrow")} title={t("projects.title")} image={IMAGES.hero2} />
      <section className="bg-offwhite py-20 md:py-28" data-testid="projects-gallery">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t("projects.filterLocation")}>
              <span className="mr-2 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/40">{t("projects.filterLocation")}</span>
              {PROJECT_LOCATIONS.map((loc) => (
                <FilterBtn key={loc} testid={`filter-location-${loc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  active={location === loc} onClick={() => setLocation(loc)}>
                  {loc === "All" ? t("projects.all") : loc}
                </FilterBtn>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t("projects.filterType")}>
              <span className="mr-2 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/40">{t("projects.filterType")}</span>
              {PROJECT_TYPES.map((tp) => (
                <FilterBtn key={tp} testid={`filter-type-${tp.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  active={type === tp} onClick={() => setType(tp)}>
                  {tp === "All" ? t("projects.all") : tp}
                </FilterBtn>
              ))}
            </div>
          </div>

          {projects === null ? (
            <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => <div key={i} className="aspect-[4/3] animate-pulse rounded-2xl bg-charcoal/5" />)}
            </div>
          ) : filtered.length > 0 ? (
            <div className="mt-14 columns-1 gap-8 sm:columns-2 lg:columns-3" data-testid="projects-grid">
              {filtered.map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
            </div>
          ) : (
            <div className="mt-14 rounded-3xl border border-charcoal/10 bg-white p-16 text-center" data-testid="projects-empty">
              <p className="mx-auto max-w-md text-sm leading-relaxed text-charcoal/60">{t("projects.empty")}</p>
              <ConsultButton testid="projects-empty-consult-button" className="mt-8" />
            </div>
          )}
        </div>
      </section>
      <FinalCta />
    </>
  );
}
